<template>
  <b-modal
    :active.sync="isModalActive"
    :width="960"
    scroll="keep"
    class="edit-activity-modal"
  >
    <div class="edit-activity-modal__card">
      <div class="edit-activity-modal__header">
        <div class="edit-activity-modal__title-row">
          <b-icon
            pack="fas"
            :icon="titleIcon"
            size="is-small"
            class="edit-activity-modal__title-icon"
          />
          <div>
            <h2 class="edit-activity-modal__title">
              {{ modalTitle }}
            </h2>
            <p v-if="modalSubtitle" class="edit-activity-modal__subtitle">
              {{ modalSubtitle }}
            </p>
          </div>
        </div>
      </div>

      <div class="edit-activity-modal__body" :class="{ 'is-loading-overlay': isLoading }">
        <b-loading :active="isLoading" :is-full-page="false" />

        <section class="edit-activity-modal__section">
          <div class="edit-activity-modal__grid">
            <b-field
              :label="$t('activity_name')"
              :type="errors.name ? 'is-danger' : undefined"
              :message="errors.name"
              :class="{ 'edit-activity-modal__field--full': !showLocationField }"
            >
              <b-input
                v-model="form.name"
                maxlength="255"
                :placeholder="$t('activity_name')"
              />
            </b-field>

            <b-field
              v-if="showLocationField"
              :label="$t('select_location')"
              :type="errors.location_id ? 'is-danger' : undefined"
              :message="errors.location_id"
            >
              <b-select
                v-model="form.location_id"
                expanded
                :placeholder="$t('select_location')"
                :disabled="isCreateSubactivity && !!parentLocationId"
              >
                <option
                  v-for="location in locationsList"
                  :key="location.id"
                  :value="location.id"
                >
                  {{ location.reference_code || location.name || location.id }}
                </option>
              </b-select>
            </b-field>

            <div v-if="!isFreeMode" class="edit-activity-modal__switches edit-activity-modal__field--full">
              <b-switch v-model="form.critical_path" type="is-success" size="is-small">
                {{ $t('critical_path') }}
              </b-switch>
              <b-switch v-model="form.in_planning" type="is-success" size="is-small">
                {{ $t('in_planning') }}
              </b-switch>
              <b-switch v-model="form.show_on_devices" type="is-success" size="is-small">
                {{ $t('show_on_devices') }}
              </b-switch>
            </div>

            <b-field v-if="!isFreeMode" :label="$t('planning_code')">
              <b-input v-model="form.planning_code" maxlength="100" />
            </b-field>

            <div>
              <b-field
                :label="$t('weight')"
                :type="errors.weight ? 'is-danger' : undefined"
                :message="errors.weight"
              >
                <b-numberinput
                  v-model="form.weight"
                  :min="0"
                  :max="weightMax"
                  :step="0.01"
                  :disabled="weightMax === 0"
                  controls-position="compact"
                  size="is-small"
                  icon-pack="fas"
                />
              </b-field>
              <div
                v-if="weightHint"
                class="edit-activity-modal__weight-hint"
                :class="`edit-activity-modal__weight-hint--${weightHint.level}`"
              >
                <b-icon
                  pack="fas"
                  :icon="weightHint.level === 'warning' ? 'exclamation-triangle' : 'info-circle'"
                  size="is-small"
                  class="edit-activity-modal__weight-hint-icon"
                />
                <span>{{ weightHint.text }}</span>
              </div>
            </div>

            <b-field v-if="!isFreeMode" :label="$t('activity_status')">
              <b-select
                v-model="form.status_id"
                expanded
                :placeholder="$t('select_status')"
              >
                <option :value="null">{{ $t('select_status') }}</option>
                <option
                  v-for="status in statusesList"
                  :key="status.id"
                  :value="status.id"
                >
                  {{ status.name }}
                </option>
              </b-select>
            </b-field>

            <b-field v-if="!isFreeMode" :label="$t('activity_type')">
              <b-select
                v-model="form.activity_type_id"
                expanded
                :placeholder="$t('activity_type')"
              >
                <option :value="null">{{ $t('activity_type') }}</option>
                <option
                  v-for="type in typesList"
                  :key="type.id"
                  :value="type.id"
                >
                  {{ type.name }}
                </option>
              </b-select>
            </b-field>
          </div>
        </section>

        <section class="edit-activity-modal__section">
          <h3 class="edit-activity-modal__section-title">{{ $t('original_planning') }}</h3>
          <div class="edit-activity-modal__grid">
            <b-field
              :label="$t('planned_start_date')"
              :type="errors.scheduled_start_date ? 'is-danger' : undefined"
              :message="errors.scheduled_start_date"
            >
              <b-datepicker
                v-model="form.scheduled_start_date"
                icon-pack="fas"
                append-to-body
                position="is-bottom-left"
                :disabled="isEditMode"
                :min-date="dateMin"
                :max-date="scheduledStartMax"
                :first-day-of-week="parseInt($t('first_day_of_week'))"
                @input="syncCurrentDatesFromScheduled"
              />
            </b-field>
            <b-field
              :label="$t('planned_end_date')"
              :type="errors.scheduled_end_date ? 'is-danger' : undefined"
              :message="errors.scheduled_end_date"
            >
              <b-datepicker
                v-model="form.scheduled_end_date"
                icon-pack="fas"
                append-to-body
                position="is-bottom-left"
                :disabled="isEditMode"
                :min-date="scheduledEndMin"
                :max-date="dateMax"
                :first-day-of-week="parseInt($t('first_day_of_week'))"
                @input="syncCurrentDatesFromScheduled"
              />
            </b-field>
          </div>
        </section>

        <section class="edit-activity-modal__section">
          <h3 class="edit-activity-modal__section-title">{{ $t('latest_planning') }}</h3>
          <div class="edit-activity-modal__grid">
            <b-field
              :label="$t('planned_start_date')"
              :type="errors.current_start_date ? 'is-danger' : undefined"
              :message="errors.current_start_date"
            >
              <b-datepicker
                :value="form.current_start_date"
                icon-pack="fas"
                append-to-body
                position="is-bottom-left"
                :disabled="!isEditMode"
                :max-date="form.current_end_date || null"
                :first-day-of-week="parseInt($t('first_day_of_week'))"
                @input="onCurrentStartDateInput"
              />
            </b-field>
            <b-field
              :label="$t('planned_end_date')"
              :type="errors.current_end_date ? 'is-danger' : undefined"
              :message="errors.current_end_date"
            >
              <b-datepicker
                :value="form.current_end_date"
                icon-pack="fas"
                append-to-body
                position="is-bottom-left"
                :disabled="!isEditMode"
                :min-date="form.current_start_date || null"
                :first-day-of-week="parseInt($t('first_day_of_week'))"
                @input="onCurrentEndDateInput"
              />
            </b-field>
          </div>
        </section>

        <section class="edit-activity-modal__section">
          <h3 class="edit-activity-modal__section-title">GANTT</h3>
          <div class="edit-activity-modal__grid">
            <b-field :label="$t('real_start_date')">
              <b-datepicker
                v-model="form.gantt_real_start_date"
                icon-pack="fas"
                append-to-body
                disabled
                :first-day-of-week="parseInt($t('first_day_of_week'))"
              />
            </b-field>
            <b-field :label="$t('real_end_date')">
              <b-datepicker
                v-model="form.gantt_real_end_date"
                icon-pack="fas"
                append-to-body
                disabled
                :first-day-of-week="parseInt($t('first_day_of_week'))"
              />
            </b-field>
            <b-field :label="$t('expected_start_date')">
              <div class="edit-activity-modal__date-with-clear">
                <b-datepicker
                  v-model="form.gantt_expected_start_date"
                  icon-pack="fas"
                  append-to-body
                  :first-day-of-week="parseInt($t('first_day_of_week'))"
                />
                <button
                  type="button"
                  class="button is-danger is-small"
                  @click="form.gantt_expected_start_date = null"
                >
                  <b-icon pack="fas" icon="trash" size="is-small" />
                </button>
              </div>
            </b-field>
            <b-field :label="$t('expected_end_date')">
              <div class="edit-activity-modal__date-with-clear">
                <b-datepicker
                  v-model="form.gantt_expected_end_date"
                  icon-pack="fas"
                  append-to-body
                  :first-day-of-week="parseInt($t('first_day_of_week'))"
                />
                <button
                  type="button"
                  class="button is-danger is-small"
                  @click="form.gantt_expected_end_date = null"
                >
                  <b-icon pack="fas" icon="trash" size="is-small" />
                </button>
              </div>
            </b-field>
          </div>
        </section>

        <section class="edit-activity-modal__section">
          <b-field :label="$t('activity_description')">
            <b-input
              v-model="form.description"
              type="textarea"
              maxlength="2000"
              rows="3"
            />
          </b-field>
        </section>

        <section class="edit-activity-modal__section">
          <div class="edit-activity-modal__users-row">
            <userlist
              ref="manager"
              title="activity_manager"
              icon="bullhorn"
              api-url="/api/v2/activity"
              :parent-entity-id="userListEntityId"
              :users-permission-type="['admin']"
              :allow-create-users="isEditMode"
              :local-mode="!isEditMode"
              :only-one-user="true"
              :allow-zero-users="true"
              :disabled="isEditMode && !isManager"
              :is-manager="isManager"
              :only-internal-users="true"
              :project-id="effectiveProjectId"
              :is-subactivity="isSubactivityContext"
              :activity-id="userListEntityId"
              role-name="activity_admin"
              class="edit-activity-modal__users-col"
              @user-added="managerAdded($event)"
              @user-removed="managerRemoved($event)"
            />
            <div class="edit-activity-modal__users-col">
              <b-field v-show="form.department" :label="$t('department')">
                <b-input :value="departmentName" disabled />
              </b-field>
            </div>
          </div>

          <div class="edit-activity-modal__users-row mt-4">
            <userlist
              ref="participants"
              title="users_participants"
              icon="users"
              api-url="/api/v2/activity"
              :parent-entity-id="userListEntityId"
              :users-permission-type="['participate']"
              :allow-create-users="false"
              :local-mode="!isEditMode"
              :only-internal-users="true"
              :project-id="effectiveProjectId"
              role-name="participant"
              class="edit-activity-modal__users-col"
              @user-added="participantAdded($event)"
            />
            <userlist
              ref="collaborators"
              title="users_collaborators"
              icon="address-book"
              api-url="/api/v2/activity"
              :parent-entity-id="userListEntityId"
              :users-permission-type="['collaborate']"
              :allow-zero-users="true"
              :allow-external-users="true"
              :allow-company-users="false"
              :allow-create-users="false"
              :local-mode="!isEditMode"
              :only-external-users="true"
              :project-id="effectiveProjectId"
              role-name="collaborator"
              class="edit-activity-modal__users-col"
              @user-added="collaboratorAdded($event)"
            />
          </div>

          <div class="edit-activity-modal__users-row mt-4">
            <userlist
              ref="viewers"
              title="viewers_users"
              icon="eye"
              api-url="/api/v2/activity"
              :parent-entity-id="userListEntityId"
              :users-permission-type="['view']"
              :local-mode="!isEditMode"
              :allow-zero-users="true"
              :only-internal-users="false"
              :allow-filter-for-role="true"
              :project-id="effectiveProjectId"
              role-name="activity_viewer"
              class="edit-activity-modal__users-col"
              @user-added="activityViewerAdded($event)"
            />
          </div>
        </section>
      </div>

      <div class="edit-activity-modal__footer">
        <button
          type="button"
          class="edit-activity-modal__cancel-btn"
          :disabled="isSaving"
          @click="close"
        >
          {{ $t('cancel') }}
        </button>
        <button
          type="button"
          class="edit-activity-modal__save-btn"
          :disabled="isSaving || isLoading"
          @click="submit"
        >
          <b-icon v-if="isSaving" pack="fas" icon="spinner" size="is-small" custom-class="fa-spin" />
          <span>{{ $t('save') }}</span>
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { mapGetters } from 'vuex'
import Userlist from '~/components/UserBox/UserList'
import EventBus from '~/plugins/bus'

const HIERARCHICAL_PARTICIPANT_EVENT = 'add-hierarchical-participant'
const PROJECT_LOCATION_CODE = 'PROJECT_LOCATION'

function roundWeight(value) {
  const number = Number(value)
  if (Number.isNaN(number)) {
    return 0
  }
  return Math.round(number * 100) / 100
}

function busListeners(eventName) {
  const events = EventBus._events || {}
  return Array.isArray(events[eventName]) ? events[eventName].slice() : []
}

function toRawDate(value) {
  if (!value) {
    return null
  }
  const parsed = moment(String(value).substring(0, 10), 'YYYY-MM-DD', true)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
}

function parseApiDate(value) {
  if (!value) {
    return null
  }
  const parsed = moment(value, ['YYYY-MM-DD', moment.ISO_8601], true)
  return parsed.isValid() ? parsed.toDate() : null
}

function formatPayloadDate(value) {
  if (!value) {
    return null
  }
  return moment(value).format('YYYY-MM-DD')
}

function emptyForm() {
  return {
    name: '',
    planning_code: '',
    weight: null,
    location_id: null,
    status_id: null,
    activity_type_id: null,
    scheduled_start_date: null,
    scheduled_end_date: null,
    current_start_date: null,
    current_end_date: null,
    gantt_real_start_date: null,
    gantt_real_end_date: null,
    gantt_expected_start_date: null,
    gantt_expected_end_date: null,
    manager_user: null,
    department: null,
    description: '',
    critical_path: false,
    in_planning: false,
    show_on_devices: true,
  }
}

export default {
  name: 'edit-activity-modal',

  components: {
    Userlist,
  },

  props: {
    projectId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      isModalActive: false,
      isLoading: false,
      isSaving: false,
      mode: 'create-activity',
      editActivityId: null,
      editActivityName: null,
      isManager: false,
      // Fechas no editables a mano: se reenvían tal cual al guardar
      editRealStartDate: null,
      editRealEndDate: null,
      editExpectedEndDate: null,
      busListenersSnapshot: null,
      parentActivityId: null,
      parentActivityName: null,
      parentLocationId: null,
      // Modo free: el emplazamiento se oculta salvo que no se haya podido resolver uno por defecto
      locationFallbackRequired: false,
      // Ponderación entre hermanas: grupo de la actividad (crear/editar actividad) o padre (subactividad)
      weightGroupId: null,
      weightUsed: null,
      // null = sin información de hermanas, no se limita
      weightAvailable: null,
      editOriginalWeight: null,
      parentDateMin: null,
      parentDateMax: null,
      form: emptyForm(),
      errors: {},
      locationsList: [],
      statusesList: [],
      typesList: [],
    }
  },

  computed: {
    ...mapGetters({
      project: 'app/project',
      authUser: 'auth/user',
    }),

    effectiveProjectId() {
      if (this.projectId != null) {
        return this.projectId
      }
      return this.project && this.project.id != null ? this.project.id : null
    },

    /** Modo libre (configs.boq_root = free en proyecto semanal). */
    isFreeMode() {
      const configs = this.project && this.project.configs
      return !!(configs && configs.progress_type === 'weekly' && configs.boq_root === 'free')
    },

    showLocationField() {
      return !this.isFreeMode || this.locationFallbackRequired
    },

    /** Emplazamiento del proyecto (Location con reference_code PROJECT_LOCATION). */
    projectLocationId() {
      if (this.project && this.project.location && this.project.location.id != null) {
        return this.project.location.id
      }
      const location = this.locationsList.find((item) => item.reference_code === PROJECT_LOCATION_CODE)
      return location ? location.id : null
    },

    /**
     * Máximo permitido: lo que dejan libre las hermanas. Al editar se respeta el peso actual
     * aunque ya supere lo disponible, para no bloquear el guardado de otros campos.
     */
    weightMax() {
      if (this.weightAvailable == null) {
        return 100
      }
      const original = this.isEditMode && this.editOriginalWeight != null ? this.editOriginalWeight : 0
      return roundWeight(Math.max(this.weightAvailable, original))
    },

    weightHint() {
      if (this.weightAvailable == null || this.errors.weight) {
        return null
      }
      if (this.weightMax === 0) {
        return { level: 'warning', text: this.$t('weight_no_available') }
      }
      return {
        level: 'info',
        text: this.$t('weight_available_info', {
          available: this.weightMax,
          used: this.weightUsed,
        }),
      }
    },

    departmentName() {
      return this.form.department && this.form.department.name
        ? this.form.department.name
        : ''
    },

    isCreateSubactivity() {
      return this.mode === 'create-subactivity'
    },

    isCreateActivity() {
      return this.mode === 'create-activity'
    },

    isEditMode() {
      return this.mode === 'edit'
    },

    isEditSubactivity() {
      return this.isEditMode && this.parentActivityId != null
    },

    isSubactivityContext() {
      return this.isCreateSubactivity || this.isEditSubactivity
    },

    /** En edición las UserList trabajan contra el backend de la actividad editada. */
    userListEntityId() {
      return this.isEditMode ? this.editActivityId : null
    },

    titleIcon() {
      if (this.isEditMode) {
        return 'edit'
      }
      return this.isCreateSubactivity ? 'sitemap' : 'plus'
    },

    modalTitle() {
      if (this.isEditMode) {
        return this.isEditSubactivity
          ? this.$t('weekly_edit_subactivity_title')
          : this.$t('activity_edit_page_title')
      }
      if (this.isCreateSubactivity) {
        return this.$t('create_subactivity')
      }
      return this.$t('add_activity')
    },

    modalSubtitle() {
      if (this.isSubactivityContext && this.parentActivityName) {
        return `${this.$t('activity')}: ${this.parentActivityName}`
      }
      return ''
    },

    dateMin() {
      return this.isSubactivityContext ? this.parentDateMin : null
    },

    dateMax() {
      return this.isSubactivityContext ? this.parentDateMax : null
    },

    scheduledStartMax() {
      if (this.form.scheduled_end_date) {
        return this.form.scheduled_end_date
      }
      return this.dateMax
    },

    scheduledEndMin() {
      if (this.form.scheduled_start_date) {
        return this.form.scheduled_start_date
      }
      return this.dateMin
    },
  },

  watch: {
    isModalActive(active) {
      if (!active) {
        this.onModalClose()
      }
    },
  },

  methods: {
    /**
     * @param {Object} options
     * @param {'create-activity'|'create-subactivity'|'edit'} options.mode
     * @param {Object|null} options.parentActivity actividad padre (crear subactividad / editar subactividad)
     * @param {Object|null} options.activity actividad o subactividad a editar (modo edit)
     * @param {Object|null} options.group grupo WBS donde se crea la actividad (ponderación entre hermanas)
     */
    show({ mode = 'create-activity', parentActivity = null, activity = null, group = null } = {}) {
      if (mode === 'edit' && !(activity && activity.id != null)) {
        return
      }
      this.mode = ['create-subactivity', 'edit'].includes(mode) ? mode : 'create-activity'
      this.editActivityId = this.isEditMode ? activity.id : null
      this.editActivityName = this.isEditMode && activity.name ? activity.name : null
      this.isManager = false
      this.editRealStartDate = null
      this.editRealEndDate = null
      this.editExpectedEndDate = null
      this.parentActivityId = parentActivity && parentActivity.id != null
        ? parentActivity.id
        : null
      this.parentActivityName = parentActivity && parentActivity.name
        ? parentActivity.name
        : null
      this.parentLocationId = null
      this.locationFallbackRequired = false
      this.weightGroupId = group && group.id != null ? group.id : null
      this.weightUsed = null
      this.weightAvailable = null
      this.editOriginalWeight = null
      this.parentDateMin = parseApiDate(parentActivity && parentActivity.current_start_date)
      this.parentDateMax = parseApiDate(parentActivity && parentActivity.current_end_date)
      this.form = emptyForm()
      this.errors = {}
      // La UserList de participantes en modo backend registra un listener global que nunca libera
      this.busListenersSnapshot = this.isEditMode ? busListeners(HIERARCHICAL_PARTICIPANT_EVENT) : null
      this.isModalActive = true
      this.$nextTick(() => {
        if (!this.isEditMode) {
          this.resetUserLists()
        }
        this.bootstrap()
      })
    },

    releaseBusListeners() {
      if (!this.busListenersSnapshot) {
        return
      }
      const previous = this.busListenersSnapshot
      busListeners(HIERARCHICAL_PARTICIPANT_EVENT)
        .filter((handler) => !previous.includes(handler))
        .forEach((handler) => EventBus.$off(HIERARCHICAL_PARTICIPANT_EVENT, handler))
      this.busListenersSnapshot = null
    },

    resetUserLists() {
      ['manager', 'participants', 'collaborators', 'viewers'].forEach((refName) => {
        const list = this.$refs[refName]
        if (list && typeof list.setUsersList === 'function') {
          list.setUsersList([])
        } else if (list) {
          list.usersList = []
        }
      })
    },

    async bootstrap() {
      if (this.isEditMode) {
        await this.bootstrapEdit()
        return
      }

      this.isLoading = true
      try {
        await Promise.all([
          this.loadLocations(),
          this.loadStatuses(),
          this.loadActivityTypes(),
          this.isCreateSubactivity ? this.loadParentActivityContext() : Promise.resolve(),
        ])

        if (this.isCreateSubactivity) {
          if (this.parentLocationId) {
            this.form.location_id = this.parentLocationId
          }
          if (this.parentDateMin) {
            this.form.scheduled_start_date = this.parentDateMin
          }
          if (this.parentDateMax) {
            this.form.scheduled_end_date = this.parentDateMax
          }
          this.syncCurrentDatesFromScheduled()
        }
        this.applyFreeModeLocation()
        await this.loadWeightAvailability()
      } finally {
        this.isLoading = false
      }

      // Responsable preseleccionado antes de cargar participantes: addUsersToParticipants excluye al responsable
      await this.$nextTick()
      await this.preselectCurrentUserAsManager()
      await this.loadProjectParticipantsAsParticipants()

      // Tras montar UserList (mismo comportamiento que edit-activity al crear subactividad):
      // responsable y creador de la actividad padre → participantes
      if (this.isCreateSubactivity && this.parentActivityId) {
        await this.$nextTick()
        await Promise.all([
          this.getActivityManagerAsParticipantUser(this.parentActivityId),
          this.getActivityCreatorAsParticipantUser(this.parentActivityId),
        ])
      }
    },

    async bootstrapEdit() {
      this.isLoading = true
      let loaded = false
      try {
        const results = await Promise.all([
          this.loadEditActivityData(),
          this.loadLocations(),
          this.isEditSubactivity ? this.loadParentActivityContext() : Promise.resolve(),
        ])
        loaded = results[0]
        if (loaded) {
          this.applyFreeModeLocation()
          await this.loadWeightAvailability()
        }
      } finally {
        this.isLoading = false
      }

      if (!loaded) {
        this.$notify.error('error_loading_activity_data')
        this.close()
      }
    },

    /** Misma carga que edit-activity.getActivityData(). */
    async loadEditActivityData() {
      try {
        const { data } = await axios.get(`/api/v2/activity/${this.editActivityId}`, {
          params: { with_statuses: 1, with_types: 1 },
        })
        if (!(data && data.success && data.data)) {
          return false
        }
        const activity = data.data
        this.form = {
          ...emptyForm(),
          name: activity.name || '',
          description: activity.description || '',
          location_id: activity.location_id || null,
          scheduled_start_date: parseApiDate(activity.scheduled_start_date),
          scheduled_end_date: parseApiDate(activity.scheduled_end_date),
          current_start_date: parseApiDate(activity.current_start_date),
          current_end_date: parseApiDate(activity.current_end_date),
          gantt_real_start_date: parseApiDate(activity.gantt_real_start_date),
          gantt_real_end_date: parseApiDate(activity.gantt_real_end_date),
          gantt_expected_start_date: parseApiDate(activity.gantt_expected_start_date),
          gantt_expected_end_date: parseApiDate(activity.gantt_expected_end_date),
          critical_path: !!activity.critical_path,
          department: activity.department || null,
          planning_code: activity.planning_code || '',
          in_planning: !!activity.in_planning,
          show_on_devices: !!activity.show_on_devices,
          weight: activity.weight != null && activity.weight !== '' ? Number(activity.weight) : null,
          status_id: activity.status_id || null,
          activity_type_id: activity.activity_type_id || null,
        }
        this.editActivityName = activity.name || this.editActivityName
        this.editOriginalWeight = this.form.weight
        if (!this.isEditSubactivity) {
          this.weightGroupId = activity.group_id || null
        }
        this.editRealStartDate = toRawDate(activity.real_start_date)
        this.editRealEndDate = toRawDate(activity.real_end_date)
        this.editExpectedEndDate = toRawDate(activity.expected_end_date)
        this.isManager = !!activity.is_manager
        this.statusesList = Array.isArray(activity.statuses) ? activity.statuses : []
        this.typesList = Array.isArray(activity.types) ? activity.types : []
        if (this.isEditSubactivity && !this.parentActivityName && activity.parent_name) {
          this.parentActivityName = activity.parent_name
        }
        return true
      } catch (error) {
        return false
      }
    },

    onCurrentStartDateInput(value) {
      const previous = this.form.current_start_date
      this.form.current_start_date = value
      if (this.isEditSubactivity && value && this.parentDateMin
        && moment(value).isBefore(this.parentDateMin, 'day')) {
        this.confirmCurrentDateOutOfParentRange('subactivity_change_current_start_date_warning', () => {
          this.form.current_start_date = previous
        })
      }
    },

    onCurrentEndDateInput(value) {
      const previous = this.form.current_end_date
      this.form.current_end_date = value
      if (this.isEditSubactivity && value && this.parentDateMax
        && moment(value).isAfter(this.parentDateMax, 'day')) {
        this.confirmCurrentDateOutOfParentRange('subactivity_change_current_end_date_warning', () => {
          this.form.current_end_date = previous
        })
      }
    },

    confirmCurrentDateOutOfParentRange(messageKey, onCancel) {
      this.$dialog.confirm({
        title: this.$t('warning'),
        message: this.$t(messageKey),
        type: 'is-warning',
        hasIcon: true,
        iconPack: 'fas',
        icon: 'exclamation-triangle',
        confirmText: this.$t('continue'),
        cancelText: this.$t('cancel'),
        onCancel,
      })
    },

    /** Modo free: sin emplazamiento propio (o del padre) se usa el del proyecto. */
    applyFreeModeLocation() {
      if (!this.isFreeMode) {
        return
      }
      if (!this.form.location_id) {
        this.form.location_id = this.projectLocationId
      }
      this.locationFallbackRequired = !this.form.location_id
    },

    syncCurrentDatesFromScheduled() {
      this.form.current_start_date = this.form.scheduled_start_date
        ? new Date(this.form.scheduled_start_date)
        : null
      this.form.current_end_date = this.form.scheduled_end_date
        ? new Date(this.form.scheduled_end_date)
        : null
    },

    async loadLocations() {
      if (!this.effectiveProjectId) {
        this.locationsList = []
        return
      }

      try {
        const { data } = await axios.get('/api/v2/locations', {
          params: { p: this.effectiveProjectId, all: true },
        })
        this.locationsList = (data && data.locations) ? data.locations : []
      } catch (error) {
        this.locationsList = []
      }
    },

    async loadStatuses() {
      if (!this.effectiveProjectId) {
        this.statusesList = []
        return
      }

      try {
        const { data } = await axios.get(`/api/v2/project/${this.effectiveProjectId}/statuses`)
        this.statusesList = (data && data.statuses) ? data.statuses : []
      } catch (error) {
        this.statusesList = []
      }
    },

    async loadActivityTypes() {
      if (!this.effectiveProjectId) {
        this.typesList = []
        return
      }

      try {
        const { data } = await axios.get('/api/v2/activity-types', {
          params: { p: this.effectiveProjectId },
        })
        this.typesList = (data && data.data) ? data.data : []
      } catch (error) {
        this.typesList = []
      }
    },

    async loadParentActivityContext() {
      if (!this.parentActivityId) {
        return
      }

      try {
        const { data } = await axios.get(`/api/v2/activity/${this.parentActivityId}`)
        if (!(data && data.success && data.data)) {
          return
        }
        const parent = data.data
        this.parentLocationId = parent.location_id || null
        this.parentDateMin = parseApiDate(parent.current_start_date)
          || parseApiDate(parent.scheduled_start_date)
          || this.parentDateMin
        this.parentDateMax = parseApiDate(parent.current_end_date)
          || parseApiDate(parent.scheduled_end_date)
          || this.parentDateMax
      } catch (error) {
        // El formulario sigue usable; location se elige a mano
      }
    },

    /** Ponderación usada por las hermanas: subactividades del padre o actividades del grupo. */
    async loadWeightAvailability() {
      if (!this.effectiveProjectId) {
        return
      }
      const params = {}
      if (this.isSubactivityContext && this.parentActivityId) {
        params.parent_id = this.parentActivityId
      } else if (!this.isSubactivityContext && this.weightGroupId) {
        params.group_id = this.weightGroupId
      } else {
        return
      }
      if (this.isEditMode && this.editActivityId) {
        params.exclude_id = this.editActivityId
      }

      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.effectiveProjectId}/activities/weight-availability`,
          { params }
        )
        if (!(data && data.success)) {
          return
        }
        this.weightUsed = roundWeight(data.used_weight)
        this.weightAvailable = roundWeight(data.available_weight)
      } catch (error) {
        // Sin datos de hermanas el peso no se limita
        return
      }

      if (!this.isEditMode && this.weightMax === 0) {
        this.form.weight = 0
      }
    },

    validate() {
      const errors = {}
      if (!String(this.form.name || '').trim()) {
        errors.name = this.$t('field_required')
      }
      const weight = this.form.weight != null && this.form.weight !== '' ? Number(this.form.weight) : null
      if (this.weightAvailable != null && weight != null && roundWeight(weight) > this.weightMax) {
        errors.weight = this.$t('weight_exceeds_available', { available: this.weightMax })
      }
      if (!this.form.location_id) {
        errors.location_id = this.$t('field_required')
      }
      if (!this.form.scheduled_start_date) {
        errors.scheduled_start_date = this.$t('field_required')
      }
      if (!this.form.scheduled_end_date) {
        errors.scheduled_end_date = this.$t('field_required')
      }
      if (this.form.scheduled_start_date && this.form.scheduled_end_date
        && moment(this.form.scheduled_end_date).isBefore(this.form.scheduled_start_date, 'day')) {
        errors.scheduled_end_date = this.$t('please_complete_required_fields')
      }
      if (this.isEditMode) {
        if (!this.form.current_start_date) {
          errors.current_start_date = this.$t('field_required')
        }
        if (!this.form.current_end_date) {
          errors.current_end_date = this.$t('field_required')
        }
        if (this.form.current_start_date && this.form.current_end_date
          && moment(this.form.current_end_date).isBefore(this.form.current_start_date, 'day')) {
          errors.current_end_date = this.$t('please_complete_required_fields')
        }
      }

      const managerList = this.$refs.manager && this.$refs.manager.usersList
        ? this.$refs.manager.usersList
        : []
      if (!managerList.length) {
        this.$notify.error('error_no_users_in_list', {
          list: this.$t('activity_manager'),
        })
        this.errors = errors
        return false
      }
      this.form.manager_user = managerList[0].user_id

      if (this.isCreateSubactivity && !this.parentActivityId) {
        this.$notify.error('error_creating_subactivity')
        return false
      }
      if (!this.effectiveProjectId) {
        this.$notify.error(this.saveErrorKey())
        return false
      }

      this.errors = errors
      return Object.keys(errors).length === 0
    },

    buildPayload() {
      const startDate = formatPayloadDate(this.form.scheduled_start_date)
      const endDate = formatPayloadDate(this.form.scheduled_end_date)
      const weight = this.form.weight != null && this.form.weight !== ''
        ? Number(this.form.weight)
        : null

      const managerList = this.$refs.manager && this.$refs.manager.usersList
        ? this.$refs.manager.usersList
        : []
      const managerUser = managerList.length
        ? managerList[0].user_id
        : this.form.manager_user

      const participants = this.$refs.participants && typeof this.$refs.participants.getUsersIds === 'function'
        ? this.$refs.participants.getUsersIds()
        : []
      const collaborators = this.$refs.collaborators && typeof this.$refs.collaborators.getUsersIds === 'function'
        ? this.$refs.collaborators.getUsersIds()
        : []

      const payload = {
        project_id: this.effectiveProjectId,
        name: String(this.form.name || '').trim(),
        location_id: this.form.location_id,
        description: String(this.form.description || '').trim() || null,
        planning_code: String(this.form.planning_code || '').trim() || null,
        weight: weight != null && !Number.isNaN(weight) ? weight : null,
        status_id: this.form.status_id || null,
        activity_type_id: this.form.activity_type_id || null,
        moment_scheduled_start_date: startDate,
        moment_scheduled_end_date: endDate,
        moment_current_start_date: startDate,
        moment_current_end_date: endDate,
        moment_real_start_date: startDate,
        moment_real_end_date: endDate,
        moment_gantt_expected_start_date: formatPayloadDate(this.form.gantt_expected_start_date),
        moment_gantt_expected_end_date: formatPayloadDate(this.form.gantt_expected_end_date),
        manager_user: managerUser,
        participants,
        collaborators,
        show_on_devices: !!this.form.show_on_devices,
        in_planning: !!this.form.in_planning,
        critical_path: this.form.critical_path ? 1 : null,
      }

      if (this.isCreateSubactivity) {
        payload.activity_id = this.parentActivityId
      }

      // Modo free: la API autogenera el planning_code al crear (EPCT_0001...) y al editar se conserva
      if (this.isFreeMode) {
        delete payload.planning_code
      }

      return payload
    },

    /** Mismos campos que envía edit-activity.sendActivityData() al editar. */
    buildEditPayload() {
      const payload = this.buildPayload()
      const formatOptional = (value) => (value ? formatPayloadDate(value) : null)

      payload.activity_id = this.isEditSubactivity ? this.parentActivityId : this.editActivityId
      if (this.isEditSubactivity) {
        payload.subactivity_id = this.editActivityId
      }
      payload.moment_current_start_date = formatPayloadDate(this.form.current_start_date)
      payload.moment_current_end_date = formatPayloadDate(this.form.current_end_date)
      // Las fechas reales no son editables a mano
      payload.moment_real_start_date = this.editRealStartDate
      payload.moment_real_end_date = this.editRealEndDate
      payload.moment_expected_end_date = this.editExpectedEndDate || this.editRealEndDate
      payload.moment_gantt_real_start_date = formatOptional(this.form.gantt_real_start_date)
      payload.moment_gantt_real_end_date = formatOptional(this.form.gantt_real_end_date)
      payload.moment_gantt_expected_start_date = formatOptional(this.form.gantt_expected_start_date)
      payload.moment_gantt_expected_end_date = formatOptional(this.form.gantt_expected_end_date)
      payload.department = this.form.department || null

      return payload
    },

    saveErrorKey() {
      if (this.isEditMode) {
        return 'error_saving_data'
      }
      return this.isCreateSubactivity ? 'error_creating_subactivity' : 'error_creating_activity'
    },

    saveSuccessKey() {
      if (this.isEditMode) {
        return this.isEditSubactivity ? 'success_editing_subactivity' : 'success_editing_activity'
      }
      return this.isCreateSubactivity ? 'success_creating_subactivity' : 'success_creating_activity'
    },

    async submit() {
      if (this.isSaving || this.isLoading) {
        return
      }
      if (!this.validate()) {
        return
      }

      this.isSaving = true
      try {
        const { data } = this.isEditMode
          ? await axios.post(`/api/v2/activity/${this.editActivityId}`, this.buildEditPayload())
          : await axios.post('/api/v2/activity', this.buildPayload())
        if (data && data.success) {
          this.$notify.success(this.saveSuccessKey())
          if (this.isEditMode) {
            this.$emit('updated', {
              mode: this.mode,
              activity_id: this.editActivityId,
              parent_activity_id: this.parentActivityId,
            })
          } else {
            this.$emit('created', {
              mode: this.mode,
              activity_id: data.activity_id,
              parent_activity_id: this.parentActivityId,
            })
          }
          this.close()
        } else {
          this.$notify.error(this.saveErrorKey())
        }
      } catch (error) {
        const apiErrors = error && error.response && error.response.data
          ? error.response.data.errors
          : null
        const firstKey = apiErrors ? Object.keys(apiErrors)[0] : null
        if (firstKey && apiErrors[firstKey] && apiErrors[firstKey][0]) {
          this.$notify.error(apiErrors[firstKey][0])
        } else {
          this.$notify.error(this.saveErrorKey())
        }
      } finally {
        this.isSaving = false
      }
    },

    /**
     * Al crear, el usuario actual queda como responsable. Mismo flujo que seleccionarlo en la UserList
     * (emite user-added → departamento y jerarquía), pero esperable para ordenar la carga de participantes.
     */
    async preselectCurrentUserAsManager() {
      const list = this.$refs.manager
      const userId = this.authUser && this.authUser.id
      if (this.isEditMode || !list || !userId || (list.usersList && list.usersList.length)) {
        return
      }
      try {
        const { data } = await axios.get(`/api/v2/user/${userId}/`, { params: { basic: 1 } })
        const user = data && data.success ? data.user : null
        if (user && this.isModalActive && !list.usersList.length && typeof list.__addUserToList === 'function') {
          list.__addUserToList(user)
        }
      } catch (error) {
        // El responsable se puede elegir a mano
      }
    },

    /** Al crear, los participantes del proyecto (rol participant) quedan precargados como participantes. */
    async loadProjectParticipantsAsParticipants() {
      if (this.isEditMode || !this.effectiveProjectId) {
        return
      }
      try {
        const { data } = await axios.get(`/api/v2/project/${this.effectiveProjectId}/users`, {
          params: { r: 'participant' },
        })
        if (data && data.success && this.isModalActive) {
          this.addUsersToParticipants(data.users_list || [])
        }
      } catch (error) {
        // Los participantes se pueden añadir a mano
      }
    },

    async managerAdded(user) {
      if (!this.isUserInList(user, this.$refs.participants, this.$refs.collaborators)) {
        try {
          const { data } = await axios.get(
            `/api/v2/project/${this.effectiveProjectId}/user/${user.user_id}/department?m=false`
          )
          if (data && data.success) {
            this.form.department = data.department
            this.getHierarchicalAsParticipantUser(data.department.id)
          }
        } catch (error) {
          // Departamento opcional en creación
        }
      } else {
        this.removeUserFromList(user, this.$refs.manager, 'activity_admin')
        this.$notify.error('error_user_in_use_manager')
      }
    },

    async managerRemoved(user) {
      this.form.department = null
      if (!user || !this.effectiveProjectId) {
        return
      }
      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.effectiveProjectId}/user/${user.user_id}/department?m=false`
        )
        if (data && data.success) {
          this.form.department = data.department
        }
      } catch (error) {
        // Mismo criterio que edit-activity: no bloquear si falla
      }
    },

    async collaboratorAdded(user) {
      if (this.isUserInList(user, this.$refs.manager, this.$refs.participants)) {
        this.removeUserFromList(user, this.$refs.collaborators, 'collaborator')
        this.$notify.error('error_user_in_use_collaborator')
      }
    },

    async participantAdded(user) {
      if (this.isUserInList(user, this.$refs.manager, this.$refs.collaborators)) {
        this.removeUserFromList(user, this.$refs.participants, 'participant')
        this.$notify.error('error_user_in_use_participant')
      }
    },

    async activityViewerAdded(user) {
      if (this.isUserInList(user, this.$refs.manager, this.$refs.participants)) {
        this.removeUserFromList(user, this.$refs.viewers, 'activity_viewer')
        this.$notify.error('error_user_in_use_collaborator')
      }
    },

    async getHierarchicalAsParticipantUser(departmentId) {
      try {
        const { data } = await axios.get(`/api/v2/department/${departmentId}/hierarchical`, {
          params: { with_dept_users: 1 },
        })
        if (data && data.success) {
          // Al editar se añaden también en el backend (igual que edit-activity)
          this.addUsersToParticipants(data.users_list || [], this.isEditMode)
        }
      } catch (error) {
        // Jerarquía opcional
      }
    },

    async getActivityManagerAsParticipantUser(activityId) {
      try {
        const { data } = await axios.get(`/api/v2/activity/${activityId}/users`, {
          params: { p: 'admin', r: 'activity_admin' },
        })
        if (data && data.success) {
          this.addUsersToParticipants(data.users_list || [])
        } else {
          this.$notify.error('error_getting_manager_user')
        }
      } catch (error) {
        this.$notify.error('error_getting_manager_user')
      }
    },

    async getActivityCreatorAsParticipantUser(activityId) {
      try {
        const { data } = await axios.get(`/api/v2/activity/${activityId}/creator`)
        if (data && data.success) {
          this.addUsersToParticipants(data.users_list || [])
        } else {
          this.$notify.error('error_getting_creator_user')
        }
      } catch (error) {
        this.$notify.error('error_getting_creator_user')
      }
    },

    addUsersToParticipants(listOrigin, addToBackend = false) {
      if (!this.$refs.participants || !Array.isArray(listOrigin)) {
        return
      }
      const participants = this.$refs.participants.usersList
      if (!Array.isArray(participants)) {
        this.$refs.participants.usersList = []
      }
      const managerUsers = this.$refs.manager && Array.isArray(this.$refs.manager.usersList)
        ? this.$refs.manager.usersList
        : []

      for (let i = listOrigin.length - 1; i >= 0; i -= 1) {
        let exist = false
        for (let j = this.$refs.participants.usersList.length - 1; j >= 0; j -= 1) {
          if (this.$refs.participants.usersList[j].user_id === listOrigin[i].user_id) {
            exist = true
            break
          }
        }
        // Misma condición que edit-activity (precedencia && / ||)
        if ((!exist && managerUsers.length === 0)
          || (!exist && managerUsers[0] && managerUsers[0].user_id !== listOrigin[i].user_id)) {
          this.$refs.participants.usersList.push(listOrigin[i])
          // Llamada directa en lugar de EventBus: evita disparar listeners de otras UserList vivas
          if (addToBackend && typeof this.$refs.participants.__addUserBackend === 'function') {
            this.$refs.participants.__addUserBackend({ user_id: listOrigin[i].user_id })
          }
        }
      }
    },

    isUserInList(user, list1, list2) {
      if (!user) {
        return false
      }
      const lists = [list1, list2]
      for (let i = 0; i < lists.length; i += 1) {
        const list = lists[i]
        const users = list && list.usersList ? list.usersList : []
        for (let j = users.length - 1; j >= 0; j -= 1) {
          if (users[j].user_id == user.user_id) {
            return true
          }
        }
      }
      return false
    },

    async removeUserFromList(user, list, roleName) {
      if (!user || !list || !list.usersList) {
        return
      }
      for (let j = list.usersList.length - 1; j >= 0; j -= 1) {
        if (list.usersList[j].user_id == user.user_id) {
          list.usersList.splice(j, 1)
          break
        }
      }

      // Solo al editar: en creación los usuarios no están en el backend hasta guardar
      if (this.isEditMode && this.editActivityId && roleName) {
        try {
          await axios.post(`/api/v2/activity/${this.editActivityId}/users/remove`, {
            user_id: user.user_id,
            role_name: roleName,
          })
        } catch (error) {
          this.$notify.error('error_remove_user_from_list')
        }
      }
    },

    close() {
      this.isModalActive = false
    },

    onModalClose() {
      const wasEditMode = this.isEditMode
      this.releaseBusListeners()
      this.form = emptyForm()
      this.errors = {}
      this.editActivityId = null
      this.editActivityName = null
      this.isManager = false
      this.editRealStartDate = null
      this.editRealEndDate = null
      this.editExpectedEndDate = null
      this.parentActivityId = null
      this.parentActivityName = null
      this.parentLocationId = null
      if (!wasEditMode) {
        this.resetUserLists()
      }
    },
  },

  beforeDestroy() {
    this.releaseBusListeners()
  },
}
</script>

<style scoped>
.edit-activity-modal__card {
  display: flex;
  flex-direction: column;
  max-height: min(92vh, 56rem);
  background-color: #fff;
  border-radius: 0.25rem;
  overflow: hidden;
}

.edit-activity-modal__header {
  flex-shrink: 0;
  padding: 0.875rem 1.25rem;
  background: linear-gradient(90deg, #2c426c 0%, #3b66bc 100%);
  color: #fff;
}

.edit-activity-modal__title-row {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
}

.edit-activity-modal__title-icon {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.edit-activity-modal__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.edit-activity-modal__subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  opacity: 0.9;
}

.edit-activity-modal__body {
  position: relative;
  flex: 1;
  min-height: 0;
  padding: 1rem 1.25rem 0.5rem;
  overflow-x: hidden;
  overflow-y: auto;
}

.edit-activity-modal__body.is-loading-overlay {
  min-height: 12rem;
  pointer-events: none;
}

.edit-activity-modal__section {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.edit-activity-modal__section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.edit-activity-modal__section-title {
  margin: 0 0 0.75rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #2c426c;
}

.edit-activity-modal__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem 1rem;
}

.edit-activity-modal__field--full {
  grid-column: 1 / -1;
}

.edit-activity-modal__switches {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  padding-top: 0.25rem;
}

.edit-activity-modal__weight-hint {
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  margin-top: -0.5rem;
  padding: 0.375rem 0.5rem;
  border: 1px solid;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.3;
}

.edit-activity-modal__weight-hint-icon {
  flex-shrink: 0;
  margin-top: 0.05rem;
}

.edit-activity-modal__weight-hint--warning {
  background-color: #fef3c7;
  border-color: #f59e0b;
  color: #92400e;
}

.edit-activity-modal__weight-hint--info {
  background-color: #eff6ff;
  border-color: #93c5fd;
  color: #1e40af;
}

.edit-activity-modal__users-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 1rem;
}

.edit-activity-modal__users-col {
  flex: 1 1 16rem;
  min-width: 0;
}

.edit-activity-modal__date-with-clear {
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
}

.edit-activity-modal__date-with-clear .datepicker,
.edit-activity-modal__date-with-clear .control {
  flex: 1;
  min-width: 0;
}

.edit-activity-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-shrink: 0;
  padding: 0.75rem 1.25rem 1rem;
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.edit-activity-modal__cancel-btn,
.edit-activity-modal__save-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.4rem 0.9rem;
  border-radius: 0.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
}

.edit-activity-modal__cancel-btn {
  border: 1px solid #d1d5db;
  background-color: #fff;
  color: #374151;
}

.edit-activity-modal__cancel-btn:hover:not(:disabled) {
  background-color: #f3f4f6;
}

.edit-activity-modal__save-btn {
  border: 1px solid #2c426c;
  background-color: #2c426c;
  color: #fff;
}

.edit-activity-modal__save-btn:hover:not(:disabled) {
  background-color: #3b66bc;
  border-color: #3b66bc;
}

.edit-activity-modal__cancel-btn:disabled,
.edit-activity-modal__save-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

@media (max-width: 640px) {
  .edit-activity-modal__grid {
    grid-template-columns: 1fr;
  }
}
</style>
